/* La'part V2: global behaviour. No dependencies. Dawn's global.js still owns cart, product form, facets, search. */
(() => {
  const root = document.documentElement;
  root.classList.add('lp-js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Offer codes in the cart: tap to copy (delegated, so it survives cart re-renders).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-lp-copy]');
    if (!btn) return;
    const code = btn.getAttribute('data-lp-copy');
    const done = () => { btn.classList.add('is-copied'); const t = btn.textContent; btn.textContent = 'Copied'; setTimeout(() => { btn.classList.remove('is-copied'); btn.textContent = t; }, 1500); };
    try { navigator.clipboard.writeText(code).then(done, () => {}); } catch (_) { /* copy is a convenience only */ }
  });

  // Overlay header is position:fixed, so keep it below the announcement bar until that scrolls away.
  const bar = document.querySelector('.lp-announcement');
  if (bar) {
    const place = () => root.style.setProperty('--lp-announce-offset', `${Math.max(0, bar.getBoundingClientRect().bottom)}px`);
    place();
    addEventListener('scroll', place, { passive: true });
    addEventListener('resize', place);
  }

  // Analytics: custom events via Shopify's pixel bus (consumed by a custom pixel if one is added). Never throws.
  const track = (name, data = {}) => {
    try { window.Shopify?.analytics?.publish?.(`lp:${name}`, data); } catch (_) { /* analytics must never break UX */ }
  };
  window.lpTrack = track;

  /* ---- Header: solid state once the hero sentinel leaves the viewport ---- */
  const header = document.querySelector('.lp-header');
  const sentinel = document.querySelector('[data-lp-header-sentinel]');
  if (header && sentinel && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => header.classList.toggle('is-solid', !entry.isIntersecting), { rootMargin: '-72px 0px 0px 0px' }).observe(sentinel);
  } else if (header) {
    header.classList.add('is-solid');
  }

  /* ---- Mega menu: one open at a time, hover intent on pointer devices, Escape + outside click close ---- */
  const megas = [...document.querySelectorAll('.lp-mega')];
  const closeAll = (except) => megas.forEach((d) => { if (d !== except) d.removeAttribute('open'); });
  const hoverCapable = window.matchMedia('(hover: hover)').matches;
  megas.forEach((d) => {
    let timer;
    d.addEventListener('toggle', () => { if (d.open) closeAll(d); });
    if (hoverCapable) {
      d.addEventListener('mouseenter', () => { clearTimeout(timer); timer = setTimeout(() => d.setAttribute('open', ''), 80); });
      d.addEventListener('mouseleave', () => { clearTimeout(timer); timer = setTimeout(() => d.removeAttribute('open'), 160); });
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = megas.find((d) => d.open);
    if (open) { open.removeAttribute('open'); open.querySelector('summary')?.focus(); }
  });
  document.addEventListener('click', (e) => { if (!e.target.closest('.lp-mega')) closeAll(); });

  /* ---- Mobile drawer: aria-hidden + inert toggle, focus in/out, scroll lock ---- */
  const drawer = document.getElementById('lp-drawer');
  if (drawer) {
    const openers = document.querySelectorAll('[aria-controls="lp-drawer"]');
    let lastFocus;
    const setOpen = (open) => {
      drawer.setAttribute('aria-hidden', String(!open));
      drawer.inert = !open;
      openers.forEach((b) => b.setAttribute('aria-expanded', String(open)));
      root.classList.toggle('lp-scroll-lock', open);
      if (open) { lastFocus = document.activeElement; drawer.querySelector('[data-lp-drawer-close]')?.focus(); } else { lastFocus?.focus(); }
    };
    drawer.inert = true;
    openers.forEach((b) => b.addEventListener('click', () => setOpen(drawer.getAttribute('aria-hidden') === 'true')));
    drawer.querySelectorAll('[data-lp-drawer-close]').forEach((el) => el.addEventListener('click', () => setOpen(false)));
    drawer.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });
    window.matchMedia('(min-width: 1024px)').addEventListener('change', (m) => { if (m.matches) setOpen(false); });
  }

  /* ---- Reveal on view ---- */
  const reveals = document.querySelectorAll('.lp-reveal');
  if (reveals.length) {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      const io = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
      }), { rootMargin: '0px 0px -8% 0px' });
      reveals.forEach((el) => io.observe(el));
    }
  }

  /* ---- Recently viewed: handles in localStorage; cards come from our own product.card template ---- */
  const RV_KEY = 'lp:recent';
  const HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const readRecent = () => {
    try { return (JSON.parse(localStorage.getItem(RV_KEY)) || []).filter((h) => typeof h === 'string' && HANDLE.test(h)); } catch (_) { return []; }
  };
  const current = document.querySelector('[data-lp-product-handle]')?.dataset.lpProductHandle;
  if (current && HANDLE.test(current)) {
    try { localStorage.setItem(RV_KEY, JSON.stringify([current, ...readRecent().filter((h) => h !== current)].slice(0, 8))); } catch (_) { /* storage unavailable */ }
  }
  document.querySelectorAll('[data-lp-recently-viewed]').forEach(async (wrap) => {
    const handles = readRecent().filter((h) => h !== current).slice(0, Number(wrap.dataset.limit || 4));
    const list = wrap.querySelector('[data-lp-recently-viewed-list]');
    if (!handles.length || !list) return;
    const base = window.Shopify?.routes?.root || '/';
    const texts = await Promise.all(handles.map((h) => fetch(`${base}products/${encodeURIComponent(h)}?view=card`).then((r) => (r.ok ? r.text() : '')).catch(() => '')));
    const cards = texts.filter(Boolean)
      .map((t) => new DOMParser().parseFromString(t, 'text/html').querySelector('.lp-card'))
      .filter(Boolean);
    if (!cards.length) return;
    list.replaceChildren(...cards.map((c) => { const li = document.createElement('li'); li.append(document.importNode(c, true)); return li; }));
    wrap.hidden = false;
  });


  /* ---- Quick-add dialog (card) ---- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-lp-quick-open]');
    if (opener) { opener.closest('.lp-card')?.querySelector('dialog.lp-quick')?.showModal(); return; }
    if (e.target.closest('[data-lp-quick-close]')) { e.target.closest('dialog')?.close(); return; }
    if (e.target.matches('dialog.lp-quick')) e.target.close(); // backdrop click
  });
  document.addEventListener('change', (e) => {
    const radio = e.target.closest('.lp-quick__opt input[type="radio"]');
    if (radio) radio.closest('form').querySelector('[data-lp-quick-id]').value = radio.value;
  });
  // Dawn declares PUB_SUB_EVENTS with const (global lexical scope, not a window property).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => document.querySelectorAll('dialog.lp-quick[open]').forEach((d) => d.close()));
  }

  /* ---- Interaction tracking (delegated) ---- */
  document.addEventListener('click', (e) => {
    const rec = e.target.closest('[data-lp-rec] a[href*="/products/"]');
    if (rec) track('recommendation_click', { list: rec.closest('[data-lp-rec]').dataset.lpRec, url: rec.getAttribute('href') });
    const quick = e.target.closest('.lp-card__quick button');
    if (quick) track('quick_add', { product: quick.closest('.lp-card')?.dataset.productHandle });
  });
  document.addEventListener('change', (e) => {
    if (e.target.closest('facet-filters-form')) track('filter_change', { name: e.target.name, value: e.target.value });
  });
  document.querySelectorAll('form[data-lp-newsletter]').forEach((f) => f.addEventListener('submit', () => track('newsletter_submit', { location: f.dataset.lpNewsletter })));

  /* ---- Casual-copy deterrent: no right-click save, drag-out or long-press save on photography ---- */
  const lpPhoto = (el) => el && el.closest && el.closest('img') && el.closest('.lp-pdp, .lp-card, .lp-hero, .lp-editorial-split, .lp-story, .lp-styling-feature, .lp-journal, .lp-cat, .lp-room, .lp-look');
  document.addEventListener('contextmenu', (e) => { if (e.target.tagName === 'IMG' && lpPhoto(e.target)) e.preventDefault(); });
  document.addEventListener('dragstart', (e) => { if (e.target.tagName === 'IMG' && lpPhoto(e.target)) e.preventDefault(); });
})();
